/*
 * Copyright (c) 2025. Slimme Infrasoftware B.V. Apeldoorn /
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden
 * volgens de voorwaarden die zijn vastgelegd in de overeenkomst
 * tussen ATR en Slimme Infrasoftware B.V. en de onderliggende
 * overeenkomsten tussen Slimme Infrasoftware B.V en
 * Spin in het Web B.V.
 */

/*
* werkbonnenlijst.ts
* 1-3-2023 - Jelmer Jellema - Spin in het Web B.V.
*/

import {Component, ViewChild} from '@angular/core';
import {Datatable2scherm} from '../abstracts/datatable2scherm.class';
import {PersoneelPlanitem} from '../../models/personeel_planitem';
import {Werkbon} from '../../models/werkbon';
import {DatatableDef, SihwDatatable} from 'sihw-ng/datatable/datatable.component';
import {Planitem} from '../../models/planitem';
import {Project} from '../../models/project';
import moment from 'moment';
import {PlanitemFunction} from '../../models/planitem_function';
import {Functie} from '../../models/functie';
import {Personeel} from '../../models/personeel';
import {Leverancier} from '../../models/leverancier';
import {inkooporder} from '../../app/types';

@Component({
    selector: 'werkbonnenlijst',
    templateUrl: './werkbonnenlijst.html',
    styleUrls: ['./werkbonnenlijst.scss']
})
export class Werkbonnenlijstscherm extends Datatable2scherm {
    _starttitle = 'Werkbonnen';
    icon = 'fas fa-file-signature';
    model = PersoneelPlanitem; //geen query maar een eigen dataGetter
    subscribeDatachanges = false; //gewoon niet
    fixedWidth = '90vw'; //om te beginnen
    openMaximized = true;

    //bereik
    datumBereikLijst: any[] = [ //aantal dagen
        {weken: 1, waarde: '1 week'},
        {weken: 4, waarde: '4 weken'},
        {weken: 0, waarde: 'Kies zelf'},
        {weken: 13, waarde: '3 maanden'},
        {weken: 26, waarde: 'Half jaar'},
        {weken: 52, waarde: 'Jaar'},
        {weken: 104, waarde: '2 jaar'},
        {weken: -1, waarde: 'Alles (duurt lang...)'},
    ];
    //9-12-2024 niet meer uit localStorage halen
    datumbereik: number = 4;
    datumkeuze: { van: string, tot: string } = {van: '', tot: ''}; //bij weken 0 is datum vrij


    public dataGewijzigd: boolean = false; //we poppen de herlaaddata-knop op als dat nodig is
    public externeProjectInfo: any = {}; //voor externe projecten
    @ViewChild('datatable') datatable: SihwDatatable;
    tableDefinition: DatatableDef = {
        track: 'id',
        standaardsortering: {
            key: 'datum',
            richting: 1
        },
        selecteer: {
            label: 'Selecteer'
        },
        rijclasses: (ppi: PersoneelPlanitem) => {
            let classes = [];
            if (!(ppi.getekend || (ppi.werkbon?.proforma))) {
                classes.push(ppi._tags.inleen ? 'missend_inleen' : 'missend');
            } else if (ppi.afwijkend || ppi.werkbon?.proforma) {
                classes.push('afwijkend');
            } else {
                classes.push('ok');
            }
            //extra classes voor selectieknoppen
            if ((!ppi.ingekocht) && (!ppi._tags.inleen) && ppi.werkbon && ppi.personeel.groep !== 'Personeel') {
                classes.push('inkoop');
            }
            //signaleer overrule door office
            if (ppi.werkbon?.starttijd_overruled || ppi.werkbon?.eindtijd_overruled) {
                classes.push('tijden_overruled');
            }
            return classes;
        },
        velden: {
            id: {
                label: 'PPI',
                type: 'int',
                hide: this.log.effectiveLoglevel !== 'debug'
            },
            pi: {
                label: 'PI',
                type: 'int',
                prop: 'planitem_id',
                hide: this.log.effectiveLoglevel !== 'debug'
            },
            weeknummer: {
                groep: 'Dienst',
                prop: 'planitem.startdate',
                label: 'Week',
                type: ['moment', {format: 'YYYY-WW'}]
            },

            datum: {
                groep: 'Dienst',
                prop: 'planitem.startdate',
                label: 'Datum',
                type: ['moment', {format: 'DD-MM-YY'}],
            },
            plantijden: {
                groep: 'Dienst',
                label: 'Gepland',
                type: 'format',
                format: ppi => ppi.planitem ? `${ppi.planitem.starttime} - ${ppi.planitem.endtime}` : ''
            },
            klant: {
                groep: 'Dienst',
                label: 'Klant',
                type: 'enum',
                format: (_project, ppi: PersoneelPlanitem) => {
                    if (ppi.planitem._tags.uitleen) {
                        const info = this.externeProjectInfo[ppi.planitem_id];
                        return info?.klantnaam ? `(uitleen) ${info.klantnaam}` : '(uitleen)';
                    }
                    return (ppi.planitem?.project as Project)?.klantnaam;
                }
            },
            project: {
                groep: 'Dienst',
                label: 'Project',
                type: 'enum',
                format: (_project, ppi: PersoneelPlanitem) => {
                    if (ppi.planitem._tags.uitleen) {
                        const info = this.externeProjectInfo[ppi.planitem_id];
                        if (info) {
                            return info.projectomschrijving ? `${info.projectomschrijving} (${info.projectnaam})` : info.projectnaam;
                        } else {
                            return '(uitleen)';
                        }
                    }
                    return (ppi.planitem?.project as Project)?.label;
                }
            },
            functie: {
                groep: 'Dienst',
                label: 'Functie',
                type: 'enum',
                prop: '_owner.functionlabel'
            },
            factureerbaar: {
                groep: 'Dienst',
                label: 'Factureerbaar',
                type: 'bool',
                prop: '_owner._owner.factureerbaar'
            },
            status: {
                groep: 'Werkbon',
                label: 'Status',
                type: 'enum',
                prop: 'werkbon.status',
                format: (status: string | undefined, ppi: PersoneelPlanitem) => {
                    if (!status) {
                        return 'Ontbreekt';
                    }
                    if (status === 'ingediend') {
                        return ppi.werkbon.proforma ? 'Ongetekend' : 'Getekend';
                    }
                    return status;
                }
            },
            tijden: {
                groep: 'Werkbon',
                label: 'Werktijden',
                type: 'format',
                format: rij => rij.werkbon ? `${rij.werkbon.gebruik_starttijd} - ${rij.werkbon.gebruik_eindtijd}` : '',
            },
            opmerkingen: {
                groep: 'Werkbon',
                label: 'Opmerking?',
                type: 'bool',
                prop: rij => (!!rij.werkbon?.opmerkingen) && rij.werkbon.opmerkingen.length > 0
            },
            kilometers: {
                groep: 'Werkbon',
                label: 'KM Project',
                type: 'int',
                prop: 'werkbon.kilometers'
            },
            kilometers_ww: {
                groep: 'Werkbon',
                label: 'KM ww',
                type: 'int',
                prop: 'werkbon.kilometers_ww'
            },
            pauze: {
                groep: 'Werkbon',
                label: 'Pauze',
                type: 'int',
                prop: 'werkbon.pauze'
            },
            personeel: {
                label: 'Personeel',
                groep: 'Personeel',
                type: 'enum',
                format: (personeel, ppi) => ppi._tags.inleen ? `(inleen) ${personeel.naam}` : personeel.naam,
            },
            leverancier: {
                label: 'Leverancier',
                prop: 'leverancier.naam',
                groep: 'Personeel',
                type: 'enum',
                format: (item, rec) => rec.personeel.groep === 'Personeel' ? '-' : (item || '-')
            },
            ingekocht: {
                label: 'Inkooporder',
                groep: 'Exact',
                type: 'bool'
            },
            ingediend: {
                groep: 'Exact',
                label: 'Ingediend',
                type: 'bool',
                standaardfilter: 2
            }

        }
    };

    //constructor ivm injection
    constructor() {
        super('debug');
    }

    /**
     * True als er iets is geselecteerd waarvoor we een proforma werkbon kunnen maken
     */
    public get kanProforma(): boolean {
        return this.datatable?.geselecteerd.some((ppi: PersoneelPlanitem) => !ppi.werkbon && !ppi._tags.inleen);
    }

    /**
     * True als er iets is geselecteerd waarvoor we een inkooporder kunnen maken
     */
    public get kanInkoopOrder(): boolean {
        return this.datatable?.geselecteerd.some((ppi: PersoneelPlanitem) => (!ppi._tags.inleen) && ppi.personeel.groep !== 'Personeel' && ppi.leverancier && (!ppi.ingekocht) && ppi.werkbon);
    }

    /**
     * True als er iets is geselecteerd waarvoor we een werkbonpdf kunnen maken
     */
    public get kanWerkbonpdf(): boolean {
        return this.datatable?.geselecteerd.some((ppi: PersoneelPlanitem) => ppi.werkbon);
    }

    public get kanIndienen(): boolean {
        return this.datatable?.geselecteerd.some((ppi: PersoneelPlanitem) => (!ppi.ingediend));
    }

    /**
     * Eigen datagetter ivm throughOwner voor het bijbehorende planitem
     */
    override async dataGetter(): Promise<PersoneelPlanitem[]> {
        const wm = this.api.werkmaatschappij;

        //we zetten eerst de van en tot
        if (this.datumbereik !== 0) {
            //0 = vrije keuze, dan blijven we er van af
            this.datumkeuze = {
                van: this.datumbereik === -1 ? '2000-01-01' : moment().subtract(this.datumbereik || 4, 'week').startOf('week').format('YYYY-MM-DD'),
                tot: moment().format('YYYY-MM-DD')
            };
        }

        this.log.debug(this.datumkeuze);

        const items: PersoneelPlanitem[] = [];
        //als we geen van en tot hebben, houdt het op met een lege array
        if (this.datumkeuze.van?.length && this.datumkeuze.tot?.length) {
            if (this.datumkeuze.tot < this.datumkeuze.van) {
                //even wisselen
                let tot = this.datumkeuze.tot;
                this.datumkeuze.tot = this.datumkeuze.van;
                this.datumkeuze.van = tot;
            }

            const ppis = (await this.api.cachedSend('werkbon', 'lijst', {
                van: this.datumkeuze.van,
                tot: this.datumkeuze.tot
            }, ['werkbon', 'planitem', 'personeelvast_planitem', 'project', 'planitem_function', 'personeelvast'])).result;
            //nog niet als PersoneelPlanitem, want daar is werk voor nodig
            //we krijgen ppis binnen met veel data eronder, al overruled en met placeholderprojecten

            const externPlanitemIds: number[] = [];

            //loop om de uitleen/inleen-status te zetten
            //wat maps om dubbel werk tegen te gaan
            const planitemMap = new Map<number, Planitem>();
            const projectMap = new Map<number, Project>();
            for (const ppidata of ppis) {
                let planitem = planitemMap.get(ppidata.planitem_id);
                if (!planitem) {
                    //dan hebben we het project ook nodig
                    let project = projectMap.get(ppidata.planitem.project_id);
                    if (!project) {
                        project = Project.create(ppidata.planitem.project);
                        project.planitem = []; //init, want is undefined in de data (verkeerdom)
                        projectMap.set(ppidata.planitem.project_id, project);
                    }
                    planitem = Planitem.create(ppidata.planitem, project);
                    planitem.personeel_planitem = []; //init, want is undefined in de data (verkeerdom)

                    project.planitem.push(planitem); //keurig opbouwen
                    planitemMap.set(ppidata.planitem_id, planitem);
                    //en zet tags
                    if (planitem.externe_werkmaatschappij === wm.id) {
                        planitem._tags.uitleen = true; //wij lenen uit op dit item
                        externPlanitemIds.push(planitem.id);
                    } else {
                        planitem._tags.uitleen = false;
                    }
                }

                const ppi = PersoneelPlanitem.create(ppidata, planitem);
                this.log.debug(ppi);
                planitem.personeel_planitem.push(ppi); //ook weer keurig opbouwen
                items.push(ppi);
            }

            //lookups op de planitems
            const functies = await Functie.getLookup(Planitem);
            const leveranciers = await Leverancier.getLookup(Planitem);
            const personeel = await Personeel.getLookup(Planitem);
            for (let planitem of planitemMap.values()) {
                //koppel de lookups
                planitem.setFunctionlabel(functies);
                planitem.setLeveranciers(leveranciers);
                planitem.setPersoneel(personeel);
            }
            //nu hebben we het personeel: nog snel de inleen
            for (const ppi of items) {
                ppi._tags.inleen = ppi.personeel.werkmaatschappij_id !== wm.id;
            }

            // Haal externe project info op indien nodig
            if (externPlanitemIds.length > 0) {
                this.externeProjectInfo = (await this.api.cachedSend('planning', 'externe_projectinfo', {planitems: externPlanitemIds})).result;
            } else {
                this.externeProjectInfo = {};
            }
        }
        this.dataGewijzigd = false; //we zijn weer bij
        this.log.debug(`Items`, items)
        return items;
    }

    /**
     * Reloadknop, omdat we niet aan subscriptions doen
     */
    public async reloadData() {
        return this.planHaalData();
    }

    /**
     * Wijziging in datumbereik: opslaan en reloaden
     */
    public async switchPeriode() {
        //9-12-2024 Niet meer in localstorage zetten
        await this.reloadData();
    }

    /**
     * Klik op een datarij: open het editscherm (op een werkbon)
     * @param ppi De geklikte ppi
     * We gebruiken dus niet de edit van de parent
     */
    async openWerkbon(ppi: PersoneelPlanitem) {
        //er moet een werkbon zijn, en we moeten toestemming hebben
        if (ppi.werkbon && this.api.can('werkbon', 'read')) {
            //werkbonedit krijgt de hele zooi binnen, met owner en al en haalt alleen missende werkbondata op
            if (await this.createWindow('Werkbonedit', {ppi: ppi}, true, true)) {
                //resultaat is true, haal data
                await this.planHaalData();
            }
        }
    }

    /**
     * Button: regel het maken van proforma werkbonnen via het backend
     * UX heeft can(...) geregeld
     */
    public async maakProforma() {
        if (!this.kanProforma) {
            return; //niets te doen
        }
        let items = <PersoneelPlanitem[]>this.datatable.geselecteerd.filter((ppi: PersoneelPlanitem) => !ppi.werkbon && !ppi._tags.inleen); //Alle items in selectie zonder werkbon
        if (!await this.api.confirm('Aanmaken proforma werkbonnen', `We maken ${items.length} ongetekende (proforma) ${items.length === 1 ? 'werkbon' : 'werkbonnen'} aan. Wil je doorgaan?`)) {
            return;
        }

        //we laten het backend de werkbonnen maken
        try {
            let aantal = (await this.api.send('werkbon', 'maakProforma', {ppis: items.map(i => i.id)})).result;
            this.api.toast((aantal === 0 || aantal > 1) ? `Er zijn ${aantal} werkbonnen gemaakt` : `De werkbon is gemaakt`, 'success');
        } catch (e) {
            this.api.toast('Aanmaken werkbonnen mislukt', 'error');
        }
        await this.reloadData();
        this.datatable.selecteerRijen([]);
    }

    /**
     * Button: regel de inkooporders via het backend. Wij sorteren voor op de juiste selectie
     */
    public async doeInkoop() {
        if (!this.kanInkoopOrder) {
            return; //niets te doen
        }
        let items = <PersoneelPlanitem[]>this.datatable.geselecteerd.filter((ppi: PersoneelPlanitem) => (!ppi._tags.inleen) && ppi.leverancier && (!ppi.ingekocht) && ppi.werkbon); //werkbon moet er zijn, maar mag proforma zijn
        //deze werkbonnen moeten. Per leverancier.
        let orders: inkooporder[] = [];
        let leveranciers: number[] = [], weken: string[] = []; //voor de statistieken
        for (let item of items) {
            let weeknummer = moment(item.planitem.startdate).format('YYYY-WW');
            if (!weken.includes(weeknummer)) {
                weken.push(weeknummer);
            }
            if (!leveranciers.includes(item.leverancier_id)) {
                leveranciers.push(item.leverancier_id);
            }
            let inkoop: inkooporder = orders.find(order => order.leverancier_id === item.leverancier_id && order.weeknummer === weeknummer);
            if (!inkoop) {
                inkoop = {
                    leverancier_id: item.leverancier_id,
                    weeknummer: weeknummer,
                    ppi: []
                };
                orders.push(inkoop);
            }
            //voeg dit item toe
            inkoop.ppi.push(item.id);
        }

        let skipwaarschuwing: string = '';
        const geskipt = this.datatable.selectieRijen - items.length;
        if (geskipt) {
            //blijkbaar gaan een paar niet
            skipwaarschuwing = ` Voor ${geskipt} geselecteerde ${geskipt === 1 ? 'rij' : 'rijen'} kunnen we geen inkooporder maken.`;
        }
        if (!await this.api.confirm('Aanmaken inkooporders', `We maken ${orders.length} ${orders.length === 1 ? 'inkooporder' : 'inkooporders'}, voor in totaal ${weken.length} ${weken.length === 1 ? 'week' : 'weken'} en ${leveranciers.length} ${leveranciers.length === 1 ? 'leverancier' : 'leveranciers'}.${skipwaarschuwing} Wil je doorgaan?`)) {
            return;
        }
        await this.api.maakInkooporders(orders); //regelt het verder wel inclusief feedback
        await this.reloadData();
    }

    /**
     * POC: regel een pdf met werkbonnen voor de geselecteerde items
     */
    public async werkbonPDF() {
        if (!this.kanWerkbonpdf) {
            return; //niets te doen
        }
        let items = (<PersoneelPlanitem[]>this.datatable.geselecteerd).filter(ppi => ppi.werkbon).map(ppi => ppi.id); //alleen de items met werkbon, getekend of niet
        //POC
        await this.api.maakWerkbonPDF(items);
    }

    /**
     * Dien de uren voor de geselecteerde regels in in exact
     */
    public async doeIndienen() {
        if (!this.kanIndienen) {
            return;
        }
        let items = <PersoneelPlanitem[]>this.datatable.geselecteerd.filter((ppi: PersoneelPlanitem) => (!ppi.ingediend));
        //nog niet ingediend
        let indienen: number[] = []; //de id's van de PPIs
        let projecten: number[] = [], zonderwerkbon: number = 0, proformawerkbon: number = 0; //statistieken
        for (let item of items) {
            let project: Project = item.planitem.project;
            if (!project) {
                continue; //kan niet voorkomen
            }
            if (!projecten.includes(project.id)) {
                projecten.push(project.id);
            }
            if (!item.werkbon) {
                zonderwerkbon++;
            } else if (!item.getekend) {
                proformawerkbon++;
            }
            indienen.push(item.id); //deze gaat naar achteren
        }
        let skipwaarschuwing: string = '';
        const geskipt = this.datatable.selectieRijen - indienen.length;
        if (geskipt) {
            //blijkbaar gaan een paar niet
            skipwaarschuwing = ` Voor ${geskipt} geselecteerde ${geskipt === 1 ? 'rij' : 'rijen'} kunnen we geen uren indienen.`;
        }
        let zonderwerkbonwaarschuwing: string = '';
        if (zonderwerkbon) {
            zonderwerkbonwaarschuwing = ` Let op. Voor ${zonderwerkbon} ${zonderwerkbon === 1 ? 'regel' : 'regels'} is er geen werkbon beschikbaar.`;
        }
        let proformawaarschuwing: string = '';
        if (proformawerkbon) {
            proformawaarschuwing = ` Let op. Voor ${proformawerkbon} ${proformawerkbon === 1 ? 'regel' : 'regels'} is er alleen een ongetekende werkbon beschikbaar.`;
        }
        if (!await this.api.confirm('Indienen uren', `We dienen ${indienen.length} ${indienen.length === 1 ? 'regel' : 'regels'} voor  ${projecten.length} ${projecten.length === 1 ? 'project' : 'projecten'} in.${skipwaarschuwing}${proformawaarschuwing}${zonderwerkbonwaarschuwing} Wil je doorgaan?`)) {
            return;
        }
        await this.api.urenIndienen(indienen); //doet ook de feedback
        //opnieuw laden, als het goed is, is er iets gewijzigd
        await this.reloadData();
        //nu mag de selectie wel weg
        this.datatable.selecteerRijen([]);
    }

    /**
     * Overrule de init: we doen niets, maar tonen alleen een knopje
     * @protected
     */
    protected initDatawijzigsubscription(): void {
        this.subscription(this.api.onDatawijziging([Project, Planitem, PersoneelPlanitem, Werkbon, PlanitemFunction, Functie, Leverancier]).subscribe(() => {
            this.dataGewijzigd = true;
        }));
    }
}
